import { ActionTree, MutationTree } from 'vuex'
import { IRootState } from '@/store'
import { DocumentActionType } from '@/store/modules/documents/interfaces/action-type'
import { DocumentMutationType } from '@/store/modules/documents/interfaces/mutation-type'
import { IDocumentsState } from '@/store/modules/documents/interfaces/IDocumenstState'
import { IDocumentsMutations } from '@/store/modules/documents/interfaces/IDocumentsMutations'
import { IDocument, IHistoryDocumentResponse } from '@/store/modules/documents/interfaces/api/IDocumentResponse'
import { documentsAPI } from '@/store/modules/documents/api/documents-api'
import { IDocumentRequest } from '@/store/modules/documents/interfaces/api/IDocumentRequest'
import { AugmentDocumentActionsType, IDocumentsActions } from '@/store/modules/documents/interfaces/IDocumentsActions'
import { ITesiDocument } from './interfaces/api/ITesiDocuments/ITesiDocumentsResponse'
import { ICablogDocument } from './interfaces/api/ICablogDocuments/ICablogDocumentsResponse'
import { ITesiDocumentsRequest } from './interfaces/api/ITesiDocuments/ITesiDocumentsRequest'
import { ICablogDocumentsRequest } from './interfaces/api/ICablogDocuments/ICablogDocumentsRequest'
import { IFilter } from '../orders/interfaces/IFilter'
import { IDropdownItems } from '../common/interface/IDropdownItems'

const state = (): IDocumentsState => ({
  documents: [],
  documentTypes: [],
  tesiDocuments: [],
  cablogDocuments: [],
  invoiceDocuments: [],
  creditDocuments: [],
  shippingDocuments: [],
  otherDocuments: [],
  loading: false,
  errors: null,
  filters: {} as IFilter,
  totalDocuments: 0
})

const mutations: MutationTree<IDocumentsState> & IDocumentsMutations = {
  [DocumentMutationType.SET_LOADING] (state: IDocumentsState, payload: boolean) {
    state.loading = payload
  },
  [DocumentMutationType.SET_DOCUMENTS] (state: IDocumentsState, payload: IDocument[]) {
    state.documents = payload
  },
  [DocumentMutationType.SET_DOCUMENT_TYPES] (state: IDocumentsState, payload: IDropdownItems[]) {
    state.documentTypes = payload
  },
  [DocumentMutationType.SET_TOTAL_DOCUMENTS] (state: IDocumentsState, payload: number) {
    state.totalDocuments = payload
  },
  [DocumentMutationType.SET_TESI_DOCUMENTS] (state: IDocumentsState, payload: ITesiDocument[]) {
    state.tesiDocuments = payload
  },
  [DocumentMutationType.SET_CABLOG_DOCUMENTS] (state: IDocumentsState, payload: ICablogDocument[]) {
    state.cablogDocuments = payload
  },
  [DocumentMutationType.SET_SHIPPING_DOCUMENTS] (state: IDocumentsState, payload: IHistoryDocumentResponse[]) {
    state.shippingDocuments = payload
  },
  [DocumentMutationType.SET_OTHER_DOCUMENTS] (state: IDocumentsState, payload: IDocument[]) {
    state.otherDocuments = payload
  },
  [DocumentMutationType.SET_INVOICE_DOCUMENTS] (state: IDocumentsState, payload: IDocument[]) {
    state.invoiceDocuments = payload
  },
  [DocumentMutationType.SET_CREDIT_DOCUMENTS] (state: IDocumentsState, payload: IDocument[]) {
    state.creditDocuments = payload
  },
  [DocumentMutationType.SET_ERROR] (state: IDocumentsState, payload: any) {
    state.errors = payload
  },
  [DocumentMutationType.SET_FILTERS] (state: IDocumentsState, payload: IFilter) {
    state.filters = payload
  }
}

const actions: ActionTree<IDocumentsState, IRootState> & IDocumentsActions = {
  async [DocumentActionType.FETCH_DOCUMENTS] ({ commit }: AugmentDocumentActionsType, dto: IDocumentRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getDocuments(dto)
      commit(DocumentMutationType.SET_DOCUMENTS, response.documentList)
      commit(DocumentMutationType.SET_TOTAL_DOCUMENTS, response.total)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
      commit(DocumentMutationType.SET_LOADING, false)
    }
  },
  async [DocumentActionType.FETCH_DOCUMENT_TYPES] ({ commit }, dto: IDocumentRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getDocumentTypes(dto)
      commit(DocumentMutationType.SET_DOCUMENT_TYPES, response.documentTypes)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
    }
    commit(DocumentMutationType.SET_LOADING, false)
  },
  async [DocumentActionType.FETCH_TESI_DOCUMENTS] ({ commit }, dto: ITesiDocumentsRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getTesiDocuments(dto)
      commit(DocumentMutationType.SET_TESI_DOCUMENTS, response.documentList)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
      commit(DocumentMutationType.SET_TESI_DOCUMENTS, [])
      commit(DocumentMutationType.SET_LOADING, false)
    }
  },
  async [DocumentActionType.FETCH_CABLOG_DOCUMENTS] ({ commit }, dto: ICablogDocumentsRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getCablogDocuments(dto)
      commit(DocumentMutationType.SET_CABLOG_DOCUMENTS, response.documentList)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
      commit(DocumentMutationType.SET_CABLOG_DOCUMENTS, [])
      commit(DocumentMutationType.SET_LOADING, false)
    }
  },

  async [DocumentActionType.FETCH_SHIPPING_DOCUMENTS] ({ commit }, dto: IDocumentRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getShippingDocuments(dto)
      commit(DocumentMutationType.SET_SHIPPING_DOCUMENTS, response)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
      commit(DocumentMutationType.SET_LOADING, false)
    }
  },

  async [DocumentActionType.FETCH_CREDIT_DOCUMENTS] ({ commit }, dto: IDocumentRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getCreditDocuments(dto)
      commit(DocumentMutationType.SET_CREDIT_DOCUMENTS, response)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
      commit(DocumentMutationType.SET_LOADING, false)
    }
  },

  async [DocumentActionType.FETCH_INVOICE_DOCUMENTS] ({ commit }, dto: IDocumentRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getInvoiceDocuments(dto)
      commit(DocumentMutationType.SET_INVOICE_DOCUMENTS, response)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
      commit(DocumentMutationType.SET_LOADING, false)
    }
  },

  async [DocumentActionType.FETCH_OTHER_DOCUMENTS] ({ commit }, dto: IDocumentRequest) {
    commit(DocumentMutationType.SET_LOADING, true)
    try {
      const response = await documentsAPI.getOtherDocuments(dto)
      commit(DocumentMutationType.SET_OTHER_DOCUMENTS, response)
    } catch (error) {
      commit(DocumentMutationType.SET_ERROR, error)
      commit(DocumentMutationType.SET_LOADING, false)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
